<script>
  import { useList } from '@hooks/use-list.svelte.js'
  import Button from '@components/button.svelte'
  import Input from '@components/global-search/input.svelte'
  import MessageBox from '@components/global-search/message-box.svelte'
  import Results from '@components/global-search/results.svelte'
  import SelectedItem from '@components/global-search/selected-item.svelte'
  import Footer from '@components/global-search/footer.svelte'
  import Settings from '@components/global-search/settings.svelte'
  import { get, post } from '@lib/requests'
  import { Turbo } from '@hotwired/turbo-rails'
  import { cn, showAlert, currentClinic } from '@lib/utils'
  import searchState from '@state/global-search-state.svelte.js'
  import { fetchFromAlgolia } from '@lib/algolia.js'
  import { Search } from 'lucide-svelte'
  // import { page } from '@inertiajs/svelte'

  // Props
  // Non reactive state
  let controller = null
  let modalContainer = $state(null)

  // let clinic = $page.props?.current_clinic
  let clinic = currentClinic()

  // TODO: We need to make sure that when a query/api call is happening that we don't allow selecting an item and we don't pile on more requests
  // Should this be handled somewhere by the underlying lib? Eg request js? Or do we need to handle it here?
  
  const list = useList({
    onQuery,
    onSelect: ({ item }) => selectResult(item)
  })

  // Reactive state
  let open = $state(false)
  let inputContainer = $state(null)

  // Derived state
  let modalClass = $derived(cn("bg-white rounded-xl shadow-lg flex flex-col w-[50rem] max-w-[90vw] h-[40rem] max-h-[90vh] z-[9998]", 
    searchState.fullScreen ? "w-full h-full" : "",
    list.navigating ? "opacity-80" : ""
  ))

  // Functions
  // async function logRequest({ type, term }) {
  //   post(`/api/v1/global_search_interactions`, { body: { type, term } })
  // }

  // onQuery function that switches between Algolia and fetchResults based on current state
  async function onQuery(q) {
    return searchState.algoliaOnly ? await fetchAlgolia(q) : await fetchResults(q)
  }

  async function fetchAlgolia(q) {
    const algoliaFilters = searchState.globalScope ? "deleted:false" : `clinic_id:${clinic?.id} AND deleted:false`
    const results = await fetchFromAlgolia(q, { filters: algoliaFilters })
    // logRequest({ type: 'algolia_request', term: q })
    return results
  }

  async function fetchResults(q) {
    try {
      const url = new URL('/api/v1/search', window.location.origin)
      url.searchParams.set('q', q)

      if (!searchState.globalScope) {
        url.searchParams.set('clinic_scoped', 'true')
      }

      const response = await get(url.toString())
      
      if (!response) return []
      return await response.json
    } catch (error) {
      console.error('fetchResults error', error)
      return []
    }
  }

  async function selectResult(item) {
    if (item?.path) {
      list.navigating = true
      await Turbo.visit(item.path)
    }
    inputContainer.focus()
  }

  function openSearch(event) {
    event.stopPropagation()
    open = true
    // logRequest({ type: 'open' })
  }

  function handleWindowKeyDown(event) {
    if (event.key === 'Escape') {
      searchState.settingsOpen ? searchState.settingsOpen = false : open = false
    } else if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
      open = true
      // logRequest({ type: 'open' })
    } else if (event.key === "," && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      searchState.settingsOpen = !searchState.settingsOpen
    }
  }

  function handleWindowClick(event) {
    if (open && modalContainer === event.target) {
      open = false
    }
  }

  // Effects
  $effect(() => {
    if (open && inputContainer) inputContainer.focus()
  })

  
  $effect(() => {
    // Every time the search state changes, set the local storage item
    localStorage.setItem('perigovet-global-search-state', JSON.stringify(searchState))
  })

  $effect(() => {
    if (open) {
      // Find any class of calendar-event and add hidden class to them
      const calendarEvents = document.querySelectorAll('.calendar-event, .time-marker')
      calendarEvents.forEach(event => {
        event.classList.add('hidden')
      })
    } else {
      // Find any class of calendar-event and remove hidden class from them
      const calendarEvents = document.querySelectorAll('.calendar-event, .time-marker')
      calendarEvents.forEach(event => {
        event.classList.remove('hidden')
      })
    }
  })

</script>

<svelte:window onkeydown={handleWindowKeyDown} onclick={handleWindowClick} />

<Button naked Icon={Search} iconSize={16} iconStrokeWidth={1.5} class="text-gray-600/80 relative -mt-[2px]" onclick={openSearch} hint="Recherche globale (⌘K)" />

{#if open}

  <!-- An overlay for the whole page -->
  <section bind:this={modalContainer} class="w-full h-full fixed top-0 left-0 bg-gray-500/60 flex items-center justify-center" style="z-index: 9999;">

    <!-- The main modal content -->
    <div class={modalClass}>

      {#if searchState.settingsOpen}
        <Settings />
      {:else}

        <!-- The search input -->
        <Input bind:this={inputContainer} {list} onToggle={() => inputContainer.focus()} />

        <!-- A two column layout (or one if no results yet) -->
        <div class="flex h-full min-h-0">

          <!-- The results list -->
          {#if list.showResults}
            <Results {list} />
          {:else}
            <MessageBox {list} />
          {/if}

          <!-- A preview column housing the preview of the selected result -->
          {#if list.highlighted && list.showResults}
            <SelectedItem item={list.highlighted} query={list.query} />
          {/if}

        </div>

        <Footer {list} bind:open />

      {/if}

    </div>
  </section>
{/if}
